import { tileAt, type MapDef } from '../world/map';
import { T } from '../world/tiles';
import { fbm, hash2, hex, mix, noise2, scale, smoothstep, type RGB } from './noise';

/**
 * Ground materials of the ximen package. Tile chars reserved for it: '4', '5' walkable, '6' solid
 * (solidity is declared in src/world/tiles.ts). Runs inside the ground worker too,
 * so keep it pure: no DOM, no game state. Return null for chars you don't own.
 *
 * '4'  Ximending: the colourful painted pedestrian street · Wanhua: worn old-town concrete
 * '5'  Ximending: the Red House's red-brick plaza · Longshan: the temple's granite slabs
 * '6'  rooftops (water tanks, AC units, tin-sheet extensions); Longshan's glazed temple roofs;
 *      on Ximending the east strip is Zhonghua Road (solid so you can't walk into the traffic)
 */
const P = {
  pave: hex('#8c8a8e'),
  paveB: hex('#9d9a9c'),
  joint: hex('#6e6c70'),
  paint: ['#ff5a8a', '#3ac8e0', '#ffd23a', '#9a6aff', '#5ad08a', '#ff8a3a'].map(hex),
  white: hex('#f4f0e8'),
  brick: hex('#a8452e'),
  brickB: hex('#c25a3a'),
  brickJ: hex('#6a3a2c'),
  slab: hex('#c9c2b4'),
  slabB: hex('#d9d2c4'),
  slabJ: hex('#8a8478'),
  concrete: hex('#a6a29a'),
  concreteB: hex('#b8b4ab'),
  oldRoad: hex('#7d7a76'),
  asphalt: hex('#45464c'),
  roof: hex('#b4ada2'),
  roofB: hex('#c4beb2'),
  tank: hex('#d8dde2'),
  ac: hex('#e4e2dc'),
  tin: ['#4a7aa8', '#3f8a8a', '#a84a3a', '#8a8f96'].map(hex),
  glaze: hex('#c8642a'),
  glazeB: hex('#e08a3a'),
  glazeD: hex('#7a3418'),
};

const own = (map: MapDef, tx: number, ty: number) => tileAt(map, tx, ty);

/** Ximending's painted street: grey pavers under big, worn patches of street art. */
function painted(x: number, y: number): RGB {
  // Small square pavers.
  const u = x % 8, v = y % 8;
  let c = mix(P.pave, P.paveB, hash2(Math.floor(x / 8), Math.floor(y / 8), 401));
  if (u < 0.5 || v < 0.5) c = P.joint;
  c = scale(c, 0.95 + noise2(x * 0.7, y * 0.7, 402) * 0.1);
  // A 4×3-tile cell of street art: stripes, dots, chevrons, a big ring or nothing.
  const cw = 64, ch = 48;
  const cx = Math.floor(x / cw), cy = Math.floor(y / ch);
  const lx = x - cx * cw, ly = y - cy * ch;
  const kind = Math.floor(hash2(cx, cy, 403) * 6);
  const col = P.paint[Math.floor(hash2(cx, cy, 404) * P.paint.length)];
  const col2 = P.paint[Math.floor(hash2(cx, cy, 405) * P.paint.length)];
  let a = 0, pc = col;
  const inset = lx > 4 && lx < cw - 4 && ly > 4 && ly < ch - 4;
  if (inset) {
    switch (kind) {
      case 0: {
        const s = Math.floor((lx + ly) / 6) % 2;
        a = 0.62;
        pc = s ? col : col2;
        break;
      }
      case 1: {
        const dx = (lx % 12) - 6, dy = (ly % 12) - 6;
        if (dx * dx + dy * dy < 12) a = 0.7;
        break;
      }
      case 2: {
        const k = (Math.abs(lx - cw / 2) + ly) % 14;
        if (k < 5) a = 0.66;
        else if (k < 7) (a = 0.5), (pc = P.white);
        break;
      }
      case 3: {
        const d = Math.hypot(lx - cw / 2, ly - ch / 2);
        if (d > 12 && d < 18) a = 0.7;
        else if (d < 7) (a = 0.6), (pc = col2);
        break;
      }
      default:
        break;
    }
  }
  if (a) {
    // Worn by thousands of sneakers: the paint is patchy.
    const wear = fbm(x / 9, y / 9, 406);
    a *= smoothstep(0.25, 0.5, wear);
    c = mix(c, pc, a);
  }
  // Round manhole covers here and there.
  const mx = Math.floor(x / 96), my = Math.floor(y / 96);
  if (hash2(mx, my, 407) < 0.35) {
    const ox = mx * 96 + 20 + hash2(mx, my, 408) * 50, oy = my * 96 + 20 + hash2(mx, my, 409) * 50;
    const d = Math.hypot(x - ox, y - oy);
    if (d < 5.5) c = d > 4.8 ? hex('#3a3a3e') : (Math.floor((x - ox + 10) / 1.5) % 2 ? hex('#5a5a60') : hex('#4a4a50'));
  }
  return c;
}

/** Old Wanhua lanes: patched grey concrete with gutters along the house fronts. */
function oldStreet(map: MapDef, x: number, y: number): RGB {
  let c = mix(P.oldRoad, P.concrete, fbm(x / 28, y / 28, 411));
  c = scale(c, 0.93 + noise2(x * 1.2, y * 1.2, 412) * 0.12);
  const patch = fbm(x / 40 + 7, y / 40, 413);
  if (patch > 0.62) c = mix(c, P.asphalt, 0.45);
  if (hash2(Math.floor(x * 2), Math.floor(y * 2), 414) < 0.03) c = scale(c, 1.2);
  // Gutter with grate where a lane meets a house front.
  const tx = Math.floor(x / T), ty = Math.floor(y / T);
  if (own(map, tx, ty - 1) === '6' && y - ty * T < 3) return Math.floor(x) % 2 ? hex('#3a3836') : hex('#5a5652');
  // Oil stains where scooters park.
  const st = fbm(x / 6, y / 6, 415);
  if (st > 0.72) c = scale(c, 0.85);
  return c;
}

/** The Red House plaza: red bricks in a basket weave, pairs alternating direction. */
function herringbone(x: number, y: number): RGB {
  const cx = Math.floor(x / 8), cy = Math.floor(y / 8);
  const horiz = (cx + cy) % 2 === 0;
  const lx = x - cx * 8, ly = y - cy * 8;
  const k = horiz ? Math.floor(ly / 4) : Math.floor(lx / 4);
  const along = horiz ? ly % 4 : lx % 4;
  if (along < 0.5 || (horiz ? lx < 0.5 : ly < 0.5)) return mix(P.brickJ, hex('#b89a8a'), 0.45);
  const h = hash2(cx * 2 + (horiz ? 0 : k), cy * 2 + (horiz ? k : 0), 421);
  let c = mix(P.brick, P.brickB, h);
  if (h > 0.93) c = hex('#8a5040');
  c = scale(c, 0.95 + noise2(x * 0.9, y * 0.9, 422) * 0.1);
  if (along < 1.2) c = scale(c, 1.06);
  c = mix(c, hex('#e0b090'), smoothstep(0.62, 0.8, fbm(x / 40, y / 30, 423)) * 0.14);
  return c;
}

/** The temple courtyard: big pale granite slabs, polished by worshippers' feet. */
function slabs(x: number, y: number): RGB {
  const row = Math.floor(y / 16);
  const off = (row % 2) * 12;
  const col = Math.floor((x + off) / 24);
  const u = x + off - col * 24, v = y - row * 16;
  if (u < 0.6 || v < 0.6) return P.slabJ;
  let c = mix(P.slab, P.slabB, hash2(col, row, 431));
  if (hash2(Math.floor(x * 1.6), Math.floor(y * 1.6), 432) < 0.14) c = scale(c, 0.9);
  c = mix(c, hex('#fff8ea'), smoothstep(0.6, 0.78, fbm(x / 30, y / 20, 433)) * 0.18);
  if (v < 1.4) c = scale(c, 1.04);
  return c;
}

/** Zhonghua Road: asphalt, lane dashes and the double yellow centre line. */
function zhonghua(x: number, y: number): RGB {
  const lx = x - 77 * T;
  let c = scale(P.asphalt, 0.94 + noise2(x * 0.6, y * 0.6, 441) * 0.1);
  if (hash2(Math.floor(x * 2), Math.floor(y * 2), 442) < 0.06) c = scale(c, 1.25);
  if (lx < 3) return mix(hex('#c8c4ba'), hex('#8a867c'), lx / 3);
  if (lx > 9 * T - 3) return hex('#a8a49a');
  const mid = 4.5 * T;
  if (Math.abs(lx - mid + 1.2) < 0.6 || Math.abs(lx - mid - 1.2) < 0.6) return hex('#e8c040');
  for (const d of [2.25 * T, 6.75 * T]) if (Math.abs(lx - d) < 0.6 && y % 24 < 12) return mix(c, P.white, 0.85);
  return c;
}

/** Flat city rooftops seen from above: concrete, water tanks, AC units, tin-sheet add-ons. */
function rooftop(map: MapDef, x: number, y: number): RGB {
  const tx = Math.floor(x / T), ty = Math.floor(y / T);
  const lx = x - tx * T, ly = y - ty * T;
  // Buildings are 3×3-tile lots; each lot gets one look.
  const bx = Math.floor(tx / 3), by = Math.floor(ty / 3);
  const lot = hash2(bx, by, 451);
  const tin = lot < 0.28;
  let c: RGB;
  if (tin) {
    // Corrugated iron sheet (鐵皮), the classic Taiwanese rooftop extension.
    const base = P.tin[Math.floor(hash2(bx, by, 452) * P.tin.length)];
    const rib = (x % 4) < 1.2 ? 1.12 : (x % 4) > 3 ? 0.86 : 1;
    c = scale(base, rib * (0.92 + noise2(x * 0.2, y * 0.5, 453) * 0.12));
    const rust = fbm(x / 12, y / 12, 454);
    if (rust > 0.66) c = mix(c, hex('#8a4a2a'), (rust - 0.66) * 1.6);
    if ((y - by * 3 * T) % 24 < 0.8) c = scale(c, 0.7);
  } else {
    c = mix(P.roof, P.roofB, fbm(x / 20, y / 20, 455));
    c = scale(c, 0.94 + noise2(x * 1.1, y * 1.1, 456) * 0.1);
    // Rain stains.
    if (fbm(x / 10, y / 7, 457) > 0.65) c = scale(c, 0.86);
    // Features on single tiles.
    const f = hash2(tx, ty, 458);
    if (f < 0.05) {
      // Stainless water tank (round, seen from above).
      const d = Math.hypot(lx - 8, ly - 8);
      if (d < 5) c = d > 4.3 ? hex('#6a7078') : mix(hex('#a8b0b8'), hex('#e8ecef'), Math.max(0, 1 - Math.hypot(lx - 6.5, ly - 6.5) / 5) * 0.8);
      else if (d < 6) c = scale(c, 0.72);
    } else if (f < 0.16) {
      // Air-conditioner condensers with a fan grille.
      if (lx > 2 && lx < 14 && ly > 3 && ly < 13) {
        c = P.ac;
        const d = Math.hypot(lx - 8, ly - 8);
        if (d < 3.6) c = Math.floor(Math.atan2(ly - 8, lx - 8) * 2.5) % 2 ? hex('#4a4e54') : hex('#6a6e74');
        if (ly > 12) c = scale(c, 0.7);
      } else if (lx > 2 && lx < 15 && ly >= 13 && ly < 15) c = scale(c, 0.75);
    } else if (f < 0.24) {
      // Potted plants on the roof garden.
      const d = Math.hypot(lx - 5 - (f * 40) % 6, ly - 8);
      if (d < 3.5) c = mix(hex('#3f7a35'), hex('#6aa84a'), noise2(x, y, 459));
      else if (d < 4.4) c = hex('#8a4a2a');
    } else if (f < 0.28) {
      // Solar water heater panel.
      if (lx > 1 && lx < 15 && ly > 2 && ly < 12) c = (Math.floor(lx) % 3 === 0) ? hex('#c8ccd0') : hex('#2a4a7a');
    }
  }
  // Parapet: a light rim and a dark drop shadow where the roof ends.
  const edge = (dx: number, dy: number) => own(map, tx + dx, ty + dy) !== '6';
  const lot0 = (tx % 3 === 0 && !tin) || (ty % 3 === 0 && !tin);
  if ((edge(0, -1) && ly < 1.6) || (edge(-1, 0) && lx < 1.6) || (edge(1, 0) && lx > T - 1.6)) return hex('#d8d2c6');
  if (edge(0, 1) && ly > T - 2) return hex('#d8d2c6');
  if (lot0 && (lx < 0.8 || ly < 0.8)) c = scale(c, 0.8);
  return c;
}

/** Longshan's temple roofs from above: glazed orange tiles in curved rows. */
function templeRoof(x: number, y: number): RGB {
  const row = Math.floor(y / 4);
  const off = (row % 2) * 2.5;
  const i = Math.floor((x + off) / 5);
  const u = (x + off) - i * 5, v = y - row * 4;
  let c = mix(P.glaze, P.glazeB, hash2(i, row, 461));
  // Each tile is a little barrel: bright crest, dark troughs.
  const crest = 1 - Math.abs(u - 2.5) / 2.5;
  c = scale(c, 0.72 + crest * 0.42);
  if (v > 3.2) c = P.glazeD;
  // Green-glazed ridge lines every 3 tiles.
  if (y % 48 < 3) c = mix(hex('#3a8a5a'), hex('#5ab07a'), crest);
  return c;
}

export function colorAtXimen(map: MapDef, x: number, y: number, mat: string): RGB | null {
  switch (mat) {
    case '4':
      return map.id === 'longshan' ? oldStreet(map, x, y) : painted(x, y);
    case '5':
      return map.id === 'longshan' ? slabs(x, y) : herringbone(x, y);
    case '6': {
      if (map.id === 'ximen' && x >= 77 * T && x < 86 * T) return zhonghua(x, y);
      if (map.id === 'longshan' && x >= 26 * T && x < 62 * T && y < 32 * T) return templeRoof(x, y);
      return rooftop(map, x, y);
    }
    default:
      return null;
  }
}

/** Flat colours shown while the detailed ground is still rendering. */
export const PLACEHOLDER_XIMEN: Record<string, string> = { '4': '#8c8a8e', '5': '#b0503a', '6': '#b4ada2' };
